import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';

import { AppError, type AppErrorKind } from '../../errors/app-error';
import { ProfileNotReadyError } from '../../auth/profile-not-ready.error';
import { IS_API_REQUEST } from '../api-context';

/**
 * Convert raw `HttpErrorResponse`s coming back from the API into typed
 * `AppError`s so feature code never has to discriminate on status codes by
 * hand.
 */
export const errorNormaliserInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.context.get(IS_API_REQUEST)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // Profile-not-ready surfaces from `/users/me` immediately after Auth0
      // creates a user; the auth service retries internally.
      if (isProfileNotReady(error)) {
        return throwError(() => new ProfileNotReadyError(error));
      }

      const kind = kindFor(error.status);
      const message = extractMessage(error) ?? defaultMessageFor(kind);
      return throwError(
        () =>
          new AppError(kind, message, {
            status: error.status,
            cause: error,
            details: isRecord(error.error) ? error.error : null,
          }),
      );
    }),
  );
};

function kindFor(status: number): AppErrorKind {
  if (status === 0) return 'network';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

function defaultMessageFor(kind: AppErrorKind): string {
  switch (kind) {
    case 'network':
      return 'Network error';
    case 'unauthorized':
      return 'You need to log in to do that';
    case 'forbidden':
      return 'You do not have permission to do that';
    case 'not_found':
      return 'Not found';
    case 'conflict':
      return 'Conflict — refresh and try again';
    case 'validation':
      return 'Validation failed';
    case 'server':
      return 'Server error';
    default:
      return 'Unexpected error';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function extractMessage(error: HttpErrorResponse): string | null {
  const body = error.error;
  if (!isRecord(body)) return null;
  const message = body['message'];
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message.every((m) => typeof m === 'string')) {
    return (message as string[]).join('; ');
  }
  return null;
}

function isProfileNotReady(error: HttpErrorResponse): boolean {
  if (error.status !== 404) return false;
  const url = error.url ?? '';
  return /\/users\/me\b/.test(url);
}
